import { Controller } from '@hotwired/stimulus'
import { api, PriorityOption } from '../../api'
import { SubmissionFormType } from '../../types/common'
import util from '../../util'
import { StreamEvent, StreamEventDetail } from './stream-manager'

export type AuthoredStatus = 'draft' | 'pending'
export type ReviewedStatus = 'approved' | 'conditionally_approved' | 'declined' | 'nigo'
export type Status = AuthoredStatus | ReviewedStatus

export interface SetStatusEvent extends Event {
  params: {
    /** The value that we should set the Status to */
    setStatus: Status

    /**
     * An optional string of pipe-delimited Status values
     * @example `declined|nigo`
     */
    whenStatus?: string
  }
}

export default class SubmissionController extends Controller<HTMLElement> {
  declare formTypeValue: SubmissionFormType
  declare idValue: string
  declare identityIdValue: string
  declare lockIdValue: string
  declare priorityValue: PriorityOption
  declare statusValue: Status
  declare readonly hasStatusInputTarget: boolean
  declare readonly statusInputTarget: HTMLInputElement

  static targets = ['statusInput']

  static values = {
    formType: String,
    id: String,
    identityId: String,
    lockId: String,
    priority: String,
    status: { type: String, default: 'pending' as Status },
  }

  connect() {
    document.addEventListener('stream-manager:render-priority', this.#handleSubmissionPriorityStream)
  }

  disconnect() {
    document.removeEventListener('stream-manager:render-priority', this.#handleSubmissionPriorityStream)
  }

  #handleSubmissionPriorityStream = (event: StreamEvent) => {
    const { streamElement, fragment } = event.detail
    if (fragment.dataset.submissionId !== this.idValue) return

    switch (streamElement.action) {
      default:
        this.#replaceAction(event.detail)
    }
  }

  #replaceAction({ originalRender, fragment, streamElement }: StreamEventDetail) {
    const priorityID = util.findUUIDs(fragment.id)[0]
    const header = document.querySelector(`#header-${priorityID}`)
    if (!header) return

    this.priorityValue = (fragment.dataset.priorityValue || '') as PriorityOption
    originalRender(streamElement)
  }

  // Getters
  get #isDraft() {
    return this.statusValue === 'draft'
  }

  get isLocked() {
    return Boolean(this.lockIdValue)
  }

  // TODO: Convert this into a stimulus value. Currently we are using this value in other places to
  // determine if the identity matches as well as the reviewer for other behaviors
  get isCurrentIdentityReviewer() {
    return Boolean(this.element.dataset.isCurrentIdentityReviewer === 'true')
  }

  // Value change methods

  statusValueChanged() {
    if (this.hasStatusInputTarget) {
      this.statusInputTarget.value = this.statusValue
    }
  }

  // Public methods

  setStatus(event: SetStatusEvent) {
    const shouldSetStatus =
      'whenStatus' in event.params && event.params.whenStatus !== ''
        ? (event.params.whenStatus.split('|') as Status[]).includes(this.statusValue)
        : true

    if (!shouldSetStatus) return

    this.statusValue = event.params.setStatus
  }

  async unlock() {
    // Do not unlock if it's not locked or if the viewer is not the reviewer (only happens on edit)
    if (!this.isLocked || (!this.#isDraft && !this.isCurrentIdentityReviewer)) return

    return api.submissions.unlock({
      identityId: this.identityIdValue,
      lockId: this.lockIdValue,
      submissionId: this.idValue,
    })
  }

  async cleanup() {
    // Attempt to unlock the submission before closing the drawer
    // It will only unlock if the lock creator matches the current user
    await this.unlock()
  }
}
